/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, ReactNode } from 'react';
import { EmrAppointment, EmrEncounter } from '@/services/emrService';

/**
 * EncounterContext
 *
 * Active-visit context provided by VisitCockpit. Lets every nested write
 * surface (notes, prescriptions, lab orders, diagnoses, observations,
 * immunizations, etc.) auto-attach the encounter without prop drilling.
 *
 * Outside of an active visit (e.g. on the patient detail page) consumers
 * should use `useOptionalEncounter()` and fall back to `off_encounter_reason`.
 */

export interface EncounterContextValue {
  encounterId: string;
  appointmentId?: string | null;
  patientId: string;
  clinicId: string;
  encounter: EmrEncounter;
  appointment?: EmrAppointment | null;
  isSigned: boolean;
}

const EncounterContext = createContext<EncounterContextValue | undefined>(undefined);

export const EncounterProvider: React.FC<{
  value: EncounterContextValue;
  children: ReactNode;
}> = ({ value, children }) => (
  <EncounterContext.Provider value={value}>{children}</EncounterContext.Provider>
);

export const useEncounter = (): EncounterContextValue => {
  const ctx = useContext(EncounterContext);
  if (ctx === undefined) {
    throw new Error('useEncounter must be used within an EncounterProvider');
  }
  return ctx;
};

export const useOptionalEncounter = (): EncounterContextValue | null =>
  useContext(EncounterContext) ?? null;
